/**
 * @fileoverview Conflict-check recipient picker (MOR-991)
 *
 * Purpose: choose who a conflict check goes to. Most firms poll everyone and
 * treat it as opt-in, so "everyone" is the default and the picker is a
 * one-line summary until the practitioner opens it; firms that only poll
 * directors and managers (Shaw Gidley) untick the rest.
 *
 * `selected` is null for "everyone in the firm" and a set of user ids once a
 * subset has been chosen — the parent passes the set straight through to
 * `conflicts.create` as `recipientUserIds`.
 *
 * Used by: src/components/pre-appointment/pipeline-job-panel.tsx
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { ChevronDown, ChevronRight, Users } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Checkbox } from "@/ui/primitives/checkbox";
import { cn } from "@/utils/misc";

interface ConflictRecipientPickerProps {
  orgId: Id<"organizations">;
  /** null = everyone in the firm. */
  selected: ReadonlySet<Id<"users">> | null;
  onChange: (selected: Set<Id<"users">> | null) => void;
  className?: string;
}

function memberName(user: {
  email: string;
  firstName?: string;
  lastName?: string;
}): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name || user.email;
}

/**
 * @component
 * @example
 * <ConflictRecipientPicker orgId={orgId} selected={recipients} onChange={setRecipients} />
 */
export function ConflictRecipientPicker({
  orgId,
  selected,
  onChange,
  className,
}: ConflictRecipientPickerProps) {
  const [open, setOpen] = useState(false);
  const { data: members } = useQuery(
    convexQuery(api.organizations.queries.getOrganizationMembers, { orgId }),
  );

  const all = members ?? [];
  const total = all.length;
  const chosen = selected ? selected.size : total;
  const everyone = selected === null;

  const isChecked = (userId: Id<"users">) =>
    selected === null || selected.has(userId);

  const toggle = (userId: Id<"users">) => {
    const next = new Set(selected ?? all.map((m) => m.user._id));
    if (next.has(userId)) {
      next.delete(userId);
    } else {
      next.add(userId);
    }
    // Ticking the last box back on returns to the plain "everyone" default.
    onChange(next.size === total ? null : next);
  };

  return (
    <div className={cn("grid gap-1.5", className)}>
      <button
        type="button"
        className="flex w-fit items-center gap-1.5 text-xs text-muted-foreground underline-offset-2 hover:text-foreground"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="size-3.5" />
        ) : (
          <ChevronRight className="size-3.5" />
        )}
        <Users className="size-3.5" />
        <span>
          {everyone ? (
            <>
              Sending to everyone in the firm
              {total > 0 && ` (${total})`}
            </>
          ) : (
            <>
              Sending to {chosen} of {total} members
            </>
          )}
        </span>
        {!open && (
          <span className="text-foreground underline underline-offset-2">
            {everyone ? "Choose recipients" : "Edit"}
          </span>
        )}
      </button>

      {open && (
        <div className="grid gap-1 rounded-md border bg-background p-2">
          <div className="flex items-center justify-between gap-2 px-1 text-[11px] text-muted-foreground">
            <span>Tick who should be asked to vote.</span>
            <span className="flex gap-2">
              <button
                type="button"
                className="underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
                disabled={everyone}
                onClick={() => onChange(null)}
              >
                Select all
              </button>
              <button
                type="button"
                className="underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
                disabled={selected?.size === 0}
                onClick={() => onChange(new Set())}
              >
                Clear
              </button>
            </span>
          </div>
          <ul className="grid max-h-56 gap-0.5 overflow-y-auto">
            {all.map((member) => {
              const id = member.user._id;
              const inputId = `conflict-recipient-${id}`;
              return (
                <li key={id}>
                  <label
                    htmlFor={inputId}
                    className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-muted"
                  >
                    <Checkbox
                      id={inputId}
                      checked={isChecked(id)}
                      onCheckedChange={() => toggle(id)}
                    />
                    <span className="min-w-0 truncate">
                      {memberName(member.user)}
                    </span>
                    <span className="ml-auto shrink-0 truncate text-muted-foreground">
                      {member.user.email}
                    </span>
                  </label>
                </li>
              );
            })}
            {total === 0 && (
              <li className="px-1.5 py-1 text-xs text-muted-foreground">
                Loading members…
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
